// client/src/components/pages/LocationAnalytics/DrillColumn.tsx (converted from .js, ST-00229)
import { useMemo, useState } from 'react';
import { FaSearch, FaChevronRight, FaCheck } from 'react-icons/fa';
import type {
  DrillDimension,
  DrillDimensionItem,
} from '../../../types/clientPages/locationAnalytics';

const fmtWeight = (w: number | null | undefined): string =>
  (w || 0).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const fmtNumber = (n: number | null | undefined): string => (n || 0).toLocaleString();

/** One segmented tab of a column. */
export interface DrillColumnTab {
  key: DrillDimension;
  tab: string;
}

export interface DrillColumnProps {
  dims: DrillColumnTab[];
  activeDim: DrillDimension;
  onDimChange: (dim: DrillDimension) => void;
  /** The listed values (the server's top N by weight). */
  items: DrillDimensionItem[];
  /**
   * How many distinct values the dimension has in the scope (ANL-20). When it is
   * more than `items.length` the server cut the list, and the column says so.
   */
  total?: number;
  selectedValue: string | null;
  onDrill: (value: string) => void;
  selectMode: boolean;
  selectedSet: Set<string> | null;
  onToggleSelect: (value: string) => void;
  isFrontier: boolean;
}

// One column in the drill (a seamless section of the unified panel). Clean text
// rows — figure on the right, ticket count beneath the name. Teal is the signal
// only: the current column's top accent, the active tab, the drilled row, the
// hover chevron, a checked row.
const DrillColumn = ({
  dims,
  activeDim,
  onDimChange,
  items,
  total,
  selectedValue,
  onDrill,
  selectMode,
  selectedSet,
  onToggleSelect,
  isFrontier,
}: DrillColumnProps) => {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    if (!query.trim()) return items;
    const q = query.toLowerCase();
    return items.filter((it) => (it.value || '').toLowerCase().includes(q));
  }, [items, query]);

  // The server lists the top values by weight; say so when it had more.
  const truncated = typeof total === 'number' && total > items.length;

  return (
    <div
      className={`flex flex-col flex-shrink-0 w-80 h-full border-t-2 ${
        isFrontier ? 'border-primary-500 bg-primary-50/30' : 'border-transparent bg-surface'
      }`}
    >
      {/* Segmented tabs */}
      <div className="px-3 py-2.5 border-b border-gray-200 flex items-center gap-2">
        <div className="flex items-center gap-0.5 bg-gray-100 rounded-lg p-0.5 min-w-0">
          {dims.map((d) => (
            <button
              key={d.key}
              onClick={() => onDimChange(d.key)}
              className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-colors truncate ${
                d.key === activeDim
                  ? 'bg-surface shadow-sm text-primary-700'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {d.tab}
            </button>
          ))}
        </div>
        <span
          className="ml-auto text-xs text-gray-400 tabular-nums flex-shrink-0"
          title={
            truncated
              ? `The ${items.length} largest by weight of ${fmtNumber(total)} in this scope`
              : undefined
          }
        >
          {truncated ? `Top ${items.length} of ${fmtNumber(total)}` : items.length}
        </span>
      </div>

      {items.length > 6 && (
        <div className="px-3 pt-2.5">
          <div className="relative">
            <FaSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-300 text-xs" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter…"
              aria-label="Filter this list"
              className="pl-7 pr-2 py-1.5 text-xs border border-gray-200 rounded-md w-full bg-surface focus:outline-none focus:ring-1 focus:ring-primary-300"
            />
          </div>
          {truncated && (
            <p className="mt-1.5 text-[11px] leading-snug text-gray-400">
              Showing the largest by weight; the filter searches these {items.length} only. Pin
              another column or narrow the period to reach the rest.
            </p>
          )}
        </div>
      )}

      <div className="overflow-y-auto flex-1 min-h-0 p-2 space-y-0.5 [scrollbar-width:thin] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent">
        {filtered.length === 0 ? (
          <p className="text-center text-sm text-gray-400 py-8">No matches.</p>
        ) : (
          filtered.map((it, i) => {
            const isDrilled = selectedValue === it.value;
            const checked = !!selectedSet && selectedSet.has(it.value);
            return (
              <button
                key={`${it.value}-${i}`}
                type="button"
                onClick={() => (selectMode ? onToggleSelect(it.value) : onDrill(it.value))}
                aria-pressed={selectMode ? checked : undefined}
                aria-label={
                  selectMode
                    ? `${checked ? 'Deselect' : 'Select'} ${it.value}`
                    : `Drill into ${it.value}`
                }
                className={`group w-full text-left px-2.5 py-2 rounded-lg transition-colors ${
                  isDrilled ? 'bg-primary-50 ring-1 ring-primary-300' : 'hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-2.5">
                  {selectMode && (
                    <span
                      aria-hidden="true"
                      className={`flex items-center justify-center w-4 h-4 rounded border flex-shrink-0 ${
                        checked
                          ? 'bg-primary-500 border-primary-500 text-white'
                          : 'border-gray-300 bg-surface'
                      }`}
                    >
                      {checked && <FaCheck className="text-[8px]" />}
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate">{it.value}</p>
                    <p className="text-xs text-gray-400 tabular-nums">
                      {fmtNumber(it.tickets)} tickets
                    </p>
                  </div>
                  <span className="text-sm font-semibold text-gray-900 tabular-nums flex-shrink-0">
                    {fmtWeight(it.weight)} t
                  </span>
                  {!selectMode && (
                    <FaChevronRight className="text-gray-300 group-hover:text-primary-500 text-xs flex-shrink-0 transition-colors" />
                  )}
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

export default DrillColumn;
